import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dayjs } from "dayjs";
import { Spin } from "antd";
import { selectLogin } from "../Login/selector";
import { getAuthUser } from "../../../utils/auth";
import { useSidebarCollapse } from "../../../utils/useSidebarCollapse";
import PageHeader from "../../components/PageHeader";
import Sidebar from "../../components/Sidebar";
import AnalyticsInjectors from "./injectors";
import WelcomeBar from "./components/WelcomeBar";
import CallChartCard from "./components/CallChartCard";
import { loadAnalytics } from "./slice";
import { selectAnalytics } from "./selector";
import type {
  AnalyticsModule,
  DateRange,
  LoadAnalyticsPayload,
} from "./types";
import { ChartsRow, InnerLayout, PageContent, PageLayout } from "./style";

export default function AnalyticsPage() {
  const dispatch = useDispatch();
  const { data } = useSelector(selectLogin);
  const { data: cards, loading } = useSelector(selectAnalytics);

  const user = data ?? getAuthUser();
  const fullName = user?.full_name ?? "Guest";
  const firstName = fullName.split(" ")[0] ?? "there";

  const [collapsed, toggleCollapsed] = useSidebarCollapse();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [module, setModule] = useState<AnalyticsModule>("registration");
  const [dateRange, setDateRange] = useState<DateRange>(null);
  const [year, setYear] = useState<Dayjs | null>(null);

  const filters = useMemo<LoadAnalyticsPayload>(() => {
    if (year) return { module, year: year.year() };
    const [start, end] = dateRange ?? [null, null];
    if (start && end) {
      return {
        module,
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };
    }
    return { module };
  }, [module, dateRange, year]);

  useEffect(() => {
    dispatch(loadAnalytics(filters));
  }, [dispatch, filters]);

  function handleDateRangeChange(value: DateRange) {
    setDateRange(value);
    if (value && value[0] && value[1]) setYear(null);
  }

  function handleYearChange(value: Dayjs | null) {
    setYear(value);
    if (value) setDateRange(null);
  }

  return (
    <PageLayout hasSider>
      <AnalyticsInjectors />
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <InnerLayout>
        <PageHeader
          routeName="Analytics"
          breadcrumbLabel="Analytics"
          onMenuClick={() => setMobileNavOpen(true)}
        />
        <PageContent>
          <WelcomeBar
            name={firstName}
            module={module}
            onModuleChange={setModule}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            year={year}
            onYearChange={handleYearChange}
          />
          <Spin spinning={loading}>
            <ChartsRow gap={20} wrap="wrap">
              {(cards ?? []).map((card) => (
                <CallChartCard key={card.key} data={card} />
              ))}
            </ChartsRow>
          </Spin>
        </PageContent>
      </InnerLayout>
    </PageLayout>
  );
}
